import React, { useState, useEffect } from 'react';
import { api, downloadExport } from './App';
import { Check, Download } from './Icons';
import SpreadsheetImport from './SpreadsheetImport';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

const DASH_TOGGLES = [
  { key: 'dash_show_countdown',   label: 'Exam countdowns' },
  { key: 'dash_show_calibration', label: 'Confidence calibration alerts' },
  { key: 'dash_show_checkin',     label: 'Topics due for recall' },
  { key: 'dash_show_priority',    label: 'Priority topic list' },
  { key: 'dash_show_touched',     label: 'Recently reviewed panel' },
  { key: 'dash_show_legend',      label: 'Priority legend' },
  { key: 'dash_show_principle',   label: 'Revision principle' },
];

function Toggle({ checked, onChange }) {
  return (
    <label className="settings-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-toggle-track" />
    </label>
  );
}

export default function Settings({ onRestartTour }) {
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState({});
  const [name, setName]       = useState('');
  const [flk1Date, setFlk1Date] = useState('');
  const [flk2Date, setFlk2Date] = useState('');
  const [provider, setProvider] = useState('');
  const [mcqTarget, setMcqTarget] = useState('40');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [pwError, setPwError]     = useState('');
  const [pwSaved, setPwSaved]     = useState(false);
  const [saved, setSaved] = useState({});
  const [showImport, setShowImport] = useState(false);
  // Custom statuses
  const [customStatuses, setCustomStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  // Spaced rep intervals
  const [srRedDays, setSrRedDays] = useState('7');
  const [srAmberDays, setSrAmberDays] = useState('14');

  useEffect(() => {
    Promise.all([api('/api/settings'), api('/api/me')]).then(([s, u]) => {
      setSettings(s);
      setUser(u);
      setName(u.name || '');
      setFlk1Date(s.flk1_exam_date || (u.exam_date_flk1 ? u.exam_date_flk1.slice(0, 10) : '') || '');
      setFlk2Date(s.flk2_exam_date || (u.exam_date_flk2 ? u.exam_date_flk2.slice(0, 10) : '') || '');
      setProvider(s.material_provider || '');
      setMcqTarget(s.mcq_daily_target || '40');
      try { setCustomStatuses(JSON.parse(s.custom_statuses || '[]')); } catch { setCustomStatuses([]); }
      setSrRedDays(s.spaced_rep_red_days || '7');
      setSrAmberDays(s.spaced_rep_amber_days || '14');
    });
  }, []);

  const flash = (key) => {
    setSaved(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 2000);
  };

  const saveSetting = async (key, value) => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ [key]: value }) });
    flash(key);
  };

  const saveName = async () => {
    if (!name.trim()) return;
    await api('/api/account', { method: 'PATCH', body: JSON.stringify({ name: name.trim() }) });
    flash('name');
  };

  const saveDates = async () => {
    const settingsUp = {};
    const accountUp = {};
    if (flk1Date) { settingsUp.flk1_exam_date = flk1Date; accountUp.exam_date_flk1 = flk1Date; }
    if (flk2Date) { settingsUp.flk2_exam_date = flk2Date; accountUp.exam_date_flk2 = flk2Date; }
    await Promise.all([
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUp) }),
      api('/api/account',  { method: 'PATCH', body: JSON.stringify(accountUp) }),
    ]);
    flash('dates');
  };

  const saveMcqTarget = () => {
    const v = Math.max(1, Math.min(999, parseInt(mcqTarget) || 40)).toString();
    setMcqTarget(v);
    saveSetting('mcq_daily_target', v);
  };

  const addCustomStatus = () => {
    const s = newStatus.trim();
    if (!s || customStatuses.includes(s)) return;
    const updated = [...customStatuses, s];
    setCustomStatuses(updated);
    setNewStatus('');
    saveSetting('custom_statuses', JSON.stringify(updated));
  };

  const removeCustomStatus = (s) => {
    const updated = customStatuses.filter(x => x !== s);
    setCustomStatuses(updated);
    saveSetting('custom_statuses', JSON.stringify(updated));
  };

  const saveSpacedRep = () => {
    const red = Math.max(1, Math.min(90, parseInt(srRedDays) || 7)).toString();
    const amber = Math.max(1, Math.min(90, parseInt(srAmberDays) || 14)).toString();
    setSrRedDays(red); setSrAmberDays(amber);
    saveSetting('spaced_rep_red_days', red);
    saveSetting('spaced_rep_amber_days', amber);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    const res = await api('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
    });
    if (res.ok) {
      setPwSaved(true); setCurrentPw(''); setNewPw('');
      setTimeout(() => setPwSaved(false), 3000);
    } else {
      setPwError(res.error || 'Failed to change password');
    }
  };

  if (showImport) {
    return <SpreadsheetImport onBack={() => setShowImport(false)} onDone={() => setShowImport(false)} />;
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">Settings</div>
      </div>

      {/* ── Account ── */}
      <div className="settings-section">
        <div className="settings-section-title">Account</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Display name</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-text-input" value={name}
              onChange={e => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={e => e.key === 'Enter' && saveName()} />
            {saved['name'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Email</span>
            <span className="settings-row-key">Read-only</span>
          </div>
          <div className="settings-row-right">
            <span className="settings-muted-val">{user.email || ''}</span>
          </div>
        </div>
        <div className="settings-pw-block">
          <span className="settings-row-label" style={{ marginBottom: 8, display: 'block' }}>Change password</span>
          <form className="settings-pw-form" onSubmit={changePassword}>
            <input className="settings-text-input" type="password" placeholder="Current password"
              value={currentPw} onChange={e => setCurrentPw(e.target.value)} autoComplete="current-password" />
            <input className="settings-text-input" type="password" placeholder="New password (8+ characters)"
              value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" />
            {pwError && <span className="settings-error">{pwError}</span>}
            {pwSaved && <span className="settings-inline-saved"><Check size={12} /> Password updated</span>}
            <button className="settings-save-btn" type="submit" style={{ alignSelf: 'flex-start' }}>
              Update password
            </button>
          </form>
        </div>
      </div>

      {/* ── Exam dates ── */}
      <div className="settings-section">
        <div className="settings-section-title">Exam dates</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK1</span>
          </div>
          <div className="settings-row-right">
            <input className="settings-date-input" type="date" value={flk1Date}
              onChange={e => setFlk1Date(e.target.value)} onBlur={saveDates} />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK2</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-date-input" type="date" value={flk2Date}
              onChange={e => setFlk2Date(e.target.value)} onBlur={saveDates} />
            {saved['dates'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Study materials ── */}
      <div className="settings-section">
        <div className="settings-section-title">Study materials</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Provider</span>
            <span className="settings-row-key">Used for MCQ import guidance</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <select className="settings-select" value={provider}
              onChange={e => { setProvider(e.target.value); saveSetting('material_provider', e.target.value); }}>
              <option value="">— Not set —</option>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {saved['material_provider'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── MCQ target ── */}
      <div className="settings-section">
        <div className="settings-section-title">MCQ practice</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Daily MCQ target</span>
            <span className="settings-row-key">Questions you aim to do each day</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="999" value={mcqTarget}
              onChange={e => setMcqTarget(e.target.value)}
              onBlur={saveMcqTarget}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            {saved['mcq_daily_target'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Topic statuses ── */}
      <div className="settings-section">
        <div className="settings-section-title">Topic statuses</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Default statuses</span>
            <span className="settings-row-key">Applied to all users — cannot be removed</span>
          </div>
          <div className="settings-row-right">
            <span className="settings-muted-val" style={{ fontSize: 12 }}>Not started · Learning · Practised · Confident · Needs review</span>
          </div>
        </div>
        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div className="settings-row-left">
            <span className="settings-row-label">Custom statuses</span>
            <span className="settings-row-key">Add your own — shown after defaults in topic dropdowns</span>
          </div>
          <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {customStatuses.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
                {customStatuses.map(s => (
                  <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                    {s}
                    <button onClick={() => removeCustomStatus(s)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="settings-text-input" value={newStatus} placeholder="New status…"
                onChange={e => setNewStatus(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustomStatus()}
                style={{ width: 140 }} />
              <button className="settings-action-btn" onClick={addCustomStatus} disabled={!newStatus.trim()}>Add</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Spaced repetition ── */}
      <div className="settings-section">
        <div className="settings-section-title">Spaced repetition</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Weak topics (under 50%)</span>
            <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srRedDays}
              onChange={e => setSrRedDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Moderate topics (50–69%)</span>
            <span className="settings-row-key">Days between revisits in planner & check-in nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srAmberDays}
              onChange={e => setSrAmberDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
            {saved['spaced_rep_red_days'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      {/* ── Dashboard panels ── */}
      <div className="settings-section">
        <div className="settings-section-title">Dashboard panels</div>
        {DASH_TOGGLES.map(({ key, label }) => (
          <div key={key} className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">{label}</span>
            </div>
            <div className="settings-row-right">
              <Toggle
                checked={(settings[key] ?? '1') === '1'}
                onChange={e => {
                  const val = e.target.checked ? '1' : '0';
                  setSettings(prev => ({ ...prev, [key]: val }));
                  saveSetting(key, val);
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Data ── */}
      <div className="settings-section">
        <div className="settings-section-title">Data</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Export your data</span>
            <span className="settings-row-key">Downloads all topics and MCQ history as Excel</span>
          </div>
          <div className="settings-row-right">
            <button className="settings-action-btn" onClick={downloadExport}>
              <Download size={13} /> Export to Excel
            </button>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Import data</span>
            <span className="settings-row-key">Import confidence scores from a spreadsheet</span>
          </div>
          <div className="settings-row-right">
            <button className="settings-action-btn" onClick={() => setShowImport(true)}>
              Import spreadsheet
            </button>
          </div>
        </div>
      </div>

      {/* ── Testing ── */}
      <div className="settings-section">
        <div className="settings-section-title">Testing</div>
        <div className="settings-info-row">
          <span className="settings-info-label">New user preview</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <button className="settings-newuser-btn"
              onClick={async () => {
                await api('/api/debug/reset-new-user', { method: 'POST' });
                window.location.reload();
              }}>
              Preview as new user →
            </button>
            <span style={{ fontSize: 11, color: 'var(--hint)' }}>Resets onboarding flags only — all data is kept</span>
          </div>
        </div>
      </div>
    </div>
  );
}
